import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    Tooltip,
    useMediaQuery,
    useTheme,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Loader } from '@googlemaps/js-api-loader';
import { reserveGoogle } from '../../utils/googleQuota';
import apiClient from '../../api';

// Status palette mirrors the kanban columns in RecruitmentPage.js so the legend reads identically.
const STATUS_COLORS = {
    new: '#1976d2',
    in_review: '#f57c00',
    approved: '#2e7d32',
    hired: '#6a1b9a',
    rejected: '#d32f2f',
    archived: '#616161',
};

const STATUS_LABELS = {
    new: 'New',
    in_review: 'In Review',
    approved: 'Approved for Hire',
    hired: 'Hired',
    rejected: 'Rejected',
    archived: 'Archived',
};

// US state centroids — used as an offline fallback when the Geocoding API is unavailable
// or when an applicant only provided a state. Coordinates are rough geographic centers.
const US_STATE_CENTROIDS = {
    AL: { lat: 32.806671, lng: -86.791130 }, AK: { lat: 61.370716, lng: -152.404419 },
    AZ: { lat: 33.729759, lng: -111.431221 }, AR: { lat: 34.969704, lng: -92.373123 },
    CA: { lat: 36.116203, lng: -119.681564 }, CO: { lat: 39.059811, lng: -105.311104 },
    CT: { lat: 41.597782, lng: -72.755371 }, DE: { lat: 39.318523, lng: -75.507141 },
    FL: { lat: 27.766279, lng: -81.686783 }, GA: { lat: 33.040619, lng: -83.643074 },
    HI: { lat: 21.094318, lng: -157.498337 }, ID: { lat: 44.240459, lng: -114.478828 },
    IL: { lat: 40.349457, lng: -88.986137 }, IN: { lat: 39.849426, lng: -86.258278 },
    IA: { lat: 42.011539, lng: -93.210526 }, KS: { lat: 38.526600, lng: -96.726486 },
    KY: { lat: 37.668140, lng: -84.670067 }, LA: { lat: 31.169546, lng: -91.867805 },
    ME: { lat: 44.693947, lng: -69.381927 }, MD: { lat: 39.063946, lng: -76.802101 },
    MA: { lat: 42.230171, lng: -71.530106 }, MI: { lat: 43.326618, lng: -84.536095 },
    MN: { lat: 45.694454, lng: -93.900192 }, MS: { lat: 32.741646, lng: -89.678696 },
    MO: { lat: 38.456085, lng: -92.288368 }, MT: { lat: 46.921925, lng: -110.454353 },
    NE: { lat: 41.125370, lng: -98.268082 }, NV: { lat: 38.313515, lng: -117.055374 },
    NH: { lat: 43.452492, lng: -71.563896 }, NJ: { lat: 40.298904, lng: -74.521011 },
    NM: { lat: 34.840515, lng: -106.248482 }, NY: { lat: 42.165726, lng: -74.948051 },
    NC: { lat: 35.630066, lng: -79.806419 }, ND: { lat: 47.528912, lng: -99.784012 },
    OH: { lat: 40.388783, lng: -82.764915 }, OK: { lat: 35.565342, lng: -96.928917 },
    OR: { lat: 44.572021, lng: -122.070938 }, PA: { lat: 40.590752, lng: -77.209755 },
    RI: { lat: 41.680893, lng: -71.511780 }, SC: { lat: 33.856892, lng: -80.945007 },
    SD: { lat: 44.299782, lng: -99.438828 }, TN: { lat: 35.747845, lng: -86.692345 },
    TX: { lat: 31.054487, lng: -97.563461 }, UT: { lat: 40.150032, lng: -111.862434 },
    VT: { lat: 44.045876, lng: -72.710686 }, VA: { lat: 37.769337, lng: -78.169968 },
    WA: { lat: 47.400902, lng: -121.490494 }, WV: { lat: 38.491226, lng: -80.954453 },
    WI: { lat: 44.268543, lng: -89.616508 }, WY: { lat: 42.755966, lng: -107.302490 },
    DC: { lat: 38.897438, lng: -77.026817 },
};

function stateCentroid(stateRaw) {
    if (!stateRaw) return null;
    const code = String(stateRaw).trim().toUpperCase();
    return US_STATE_CENTROIDS[code] || null;
}

// IMPORTANT: must match the options used by client/src/components/MapComponent.js
// or @googlemaps/js-api-loader will throw "Loader must not be called again with different options".
let _sharedLoader = null;
function getLoader() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return null;
    if (!_sharedLoader) {
        _sharedLoader = new Loader({
            apiKey,
            version: 'weekly',
            libraries: ['geometry', 'places'],
        });
    }
    return _sharedLoader;
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function ApplicantMapDialog({ open, onClose, onApplicantClick }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState(null);
    const [clientPoints, setClientPoints] = useState([]); // points geocoded in-browser this session
    const [centroidPoints, setCentroidPoints] = useState([]); // approximate state-centroid fallback
    const [clientGeocodeProgress, setClientGeocodeProgress] = useState(null); // { done, total }
    const [geocodingApiUnavailable, setGeocodingApiUnavailable] = useState(false);
    const [error, setError] = useState(null);
    const [mapsReady, setMapsReady] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const googleRef = useRef(null);
    const geocoderRef = useRef(null);
    const clientGeocodeAbortRef = useRef(false);

    const apiKeyMissing = !process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const fetchPoints = async ({ refresh = false } = {}) => {
        if (refresh) setRefreshing(true); else setLoading(true);
        setError(null);
        // Abort any in-flight client geocoding from a previous session.
        clientGeocodeAbortRef.current = true;
        setClientPoints([]);
        setCentroidPoints([]);
        setClientGeocodeProgress(null);
        setGeocodingApiUnavailable(false);
        try {
            const res = await apiClient.get('/intake-admin/resumes/map-points', {
                params: refresh ? { refresh: 1 } : {},
                timeout: 60000,
            });
            setData(res.data?.data || null);
        } catch (err) {
            console.error('[ApplicantMapDialog] Failed to fetch map points:', err);
            setError(err?.response?.data?.message || err.message || 'Failed to load applicant map.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Plot every entry that has a state at the state centroid. Used when no Geocoding API is
    // available — the dots are approximate but at least the user can see distribution.
    const plotStateCentroids = (pending) => {
        const points = [];
        for (const entry of pending) {
            const c = stateCentroid(entry.address?.state);
            if (!c) continue;
            // Add a tiny jitter so multiple applicants from the same state don't perfectly stack.
            const jitter = () => (Math.random() - 0.5) * 0.4;
            points.push({
                id: entry.id,
                firstName: entry.firstName,
                lastName: entry.lastName,
                status: entry.status,
                lat: c.lat + jitter(),
                lng: c.lng + jitter(),
                formattedAddress: entry.address?.state ? `${entry.address.state} (approx.)` : 'Approximate',
                address: entry.address,
                approximate: true,
            });
        }
        return points;
    };

    // Browser-side geocoding fallback. If the Geocoding API isn't enabled / is denied, immediately
    // switch to the offline state-centroid fallback so the map isn't empty.
    const runClientGeocoding = async () => {
        if (!data || !mapsReady || !googleRef.current) return;
        const pending = Array.isArray(data.pendingClientGeocode) ? data.pendingClientGeocode : [];
        if (pending.length === 0) return;
        if (!geocoderRef.current) {
            geocoderRef.current = new googleRef.current.maps.Geocoder();
        }
        const geocoder = geocoderRef.current;
        clientGeocodeAbortRef.current = false;
        setClientGeocodeProgress({ done: 0, total: pending.length });

        const newPoints = [];
        const resultsToSave = [];
        let done = 0;
        let geocodingDenied = false;

        const geocodeOne = (address) => new Promise((resolve) => {
            geocoder.geocode({ address, region: 'us' }, (results, status) => {
                resolve({ results, status });
            });
        });

        for (const entry of pending) {
            if (clientGeocodeAbortRef.current || geocodingDenied) break;
            let matched = null;
            for (const candidate of entry.candidates) {
                if (clientGeocodeAbortRef.current || geocodingDenied) break;
                // eslint-disable-next-line no-await-in-loop
                const allowed = await reserveGoogle('geocoding');
                if (!allowed) {
                    geocodingDenied = true;
                    break;
                }
                const { results, status } = await geocodeOne(candidate);
                if (status === 'OK' && results?.[0]?.geometry?.location) {
                    const loc = results[0].geometry.location;
                    matched = {
                        lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
                        lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
                        formattedAddress: results[0].formatted_address,
                    };
                    break;
                }
                if (status === 'REQUEST_DENIED') {
                    geocodingDenied = true;
                    setGeocodingApiUnavailable(true);
                    break;
                }
                if (status === 'OVER_QUERY_LIMIT') {
                    clientGeocodeAbortRef.current = true;
                    break;
                }
                // ZERO_RESULTS / INVALID_REQUEST → try next candidate
            }
            if (matched) {
                newPoints.push({
                    id: entry.id,
                    firstName: entry.firstName,
                    lastName: entry.lastName,
                    status: entry.status,
                    lat: matched.lat,
                    lng: matched.lng,
                    formattedAddress: matched.formattedAddress,
                    address: entry.address,
                });
                resultsToSave.push({
                    id: entry.id,
                    addressHash: entry.addressHash,
                    status: 'ok',
                    lat: matched.lat,
                    lng: matched.lng,
                    formattedAddress: matched.formattedAddress,
                });
            } else if (!geocodingDenied) {
                resultsToSave.push({
                    id: entry.id,
                    addressHash: entry.addressHash,
                    status: 'zero_results',
                });
            }
            done++;
            setClientGeocodeProgress({ done, total: pending.length });
            if (newPoints.length > 0 && (done % 10 === 0 || done === pending.length)) {
                setClientPoints([...newPoints]);
            }
        }
        setClientPoints(newPoints);
        setClientGeocodeProgress(null);

        // If the Geocoding API was denied, fall back to state centroids for everything we
        // couldn't geocode. Don't persist these — they're approximate and we want real coords
        // once the API is enabled.
        if (geocodingDenied) {
            const placedIds = new Set(newPoints.map((p) => p.id));
            const remaining = pending.filter((e) => !placedIds.has(e.id));
            const approx = plotStateCentroids(remaining);
            setCentroidPoints(approx);
        }

        // Persist real geocoding results (ok / zero_results). Skip if API was denied — those
        // entries will retry on next open.
        if (!geocodingDenied && resultsToSave.length > 0) {
            try {
                await apiClient.post('/intake-admin/resumes/geocode-results', { results: resultsToSave });
            } catch (saveErr) {
                console.warn('[ApplicantMapDialog] Failed to persist geocode results:', saveErr?.message || saveErr);
            }
        }
    };

    // Load Google Maps JS once when the dialog first opens.
    useEffect(() => {
        if (!open) return;
        if (apiKeyMissing) return;
        if (mapsReady) return;
        let cancelled = false;
        const loader = getLoader();
        if (!loader) return;
        loader.load()
            .then((google) => {
                if (cancelled) return;
                googleRef.current = google;
                setMapsReady(true);
            })
            .catch((err) => {
                console.error('[ApplicantMapDialog] Google Maps load failed:', err);
                if (!cancelled) setError('Failed to load Google Maps. Please retry.');
            });
        return () => { cancelled = true; };
    }, [open, apiKeyMissing, mapsReady]);

    // Fetch points when dialog opens.
    useEffect(() => {
        if (!open) return;
        fetchPoints();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset map state when closing.
    useEffect(() => {
        if (open) return;
        clearMarkers();
        if (infoWindowRef.current) {
            try { infoWindowRef.current.close(); } catch (_) {}
        }
        mapRef.current = null;
        clientGeocodeAbortRef.current = true;
    }, [open]);

    // Once data + maps are both ready, kick off client-side geocoding for any pending entries.
    useEffect(() => {
        if (!open || !data || !mapsReady) return;
        if (!data.serverGeocodingForbidden) return;
        if (!Array.isArray(data.pendingClientGeocode) || data.pendingClientGeocode.length === 0) return;
        runClientGeocoding();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, data, mapsReady]);

    const points = useMemo(() => {
        const server = Array.isArray(data?.points) ? data.points : [];
        const seen = new Set(server.map((p) => p.id));
        const result = [...server];
        for (const p of clientPoints) {
            if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
        }
        for (const p of centroidPoints) {
            if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
        }
        return result;
    }, [data, clientPoints, centroidPoints]);

    // (Re)render the map and markers whenever we have google + container + points.
    useEffect(() => {
        if (!open) return;
        if (!mapsReady || !googleRef.current) return;
        if (!mapContainer.current) return;

        const google = googleRef.current;
        let cancelled = false;

        const startMap = async () => {
        // Initialise map once.
        if (!mapRef.current) {
            const allowed = await reserveGoogle('mapsJavascript');
            if (cancelled || !allowed) return;
            if (mapRef.current) return;
            mapRef.current = new google.maps.Map(mapContainer.current, {
                center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
                zoom: 4,
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'greedy',
            });
            infoWindowRef.current = new google.maps.InfoWindow();
        }

        clearMarkers();

        if (points.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        points.forEach((p) => {
            const color = STATUS_COLORS[p.status] || '#1976d2';
            const isApprox = !!p.approximate;
            const position = { lat: Number(p.lat), lng: Number(p.lng) };
            const marker = new google.maps.Marker({
                position,
                map: mapRef.current,
                title: `${p.firstName} ${p.lastName}`.trim() + (isApprox ? ' (approximate)' : ''),
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: isApprox ? 6 : 8,
                    fillColor: color,
                    fillOpacity: isApprox ? 0.45 : 0.9,
                    strokeColor: '#ffffff',
                    strokeWeight: isApprox ? 1 : 2,
                },
            });

            const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Applicant';
            const addrLine1 = [p.address?.street, p.address?.suite].filter(Boolean).join(', ');
            const addrLine2 = [p.address?.city, p.address?.state, p.address?.postal_code].filter(Boolean).join(', ');
            const statusLabel = STATUS_LABELS[p.status] || p.status || '';
            const approxNote = isApprox
                ? `<div style="font-size:11px;color:#b26a00;font-style:italic;margin-top:4px;">Approximate location (state centroid)</div>`
                : '';
            const popupHtml = `
                <div style="font-family: Arial, sans-serif; max-width: 260px;">
                    <div style="font-weight:600; font-size:14px; margin-bottom:4px;">${escapeHtml(fullName)}</div>
                    <div style="display:inline-block;padding:2px 8px;border-radius:10px;background:${color};color:#fff;font-size:11px;margin-bottom:6px;">${escapeHtml(statusLabel)}</div>
                    ${addrLine1 ? `<div style="font-size:12px;color:#444;">${escapeHtml(addrLine1)}</div>` : ''}
                    ${addrLine2 ? `<div style="font-size:12px;color:#444;">${escapeHtml(addrLine2)}</div>` : ''}
                    ${approxNote}
                    <button id="applicant-map-view-${escapeHtml(p.id)}" style="margin-top:8px;padding:6px 10px;border:none;background:#1976d2;color:#fff;border-radius:4px;cursor:pointer;font-size:12px;">View Details</button>
                </div>
            `;

            marker.addListener('click', () => {
                if (!infoWindowRef.current) return;
                infoWindowRef.current.close();
                infoWindowRef.current.setContent(popupHtml);
                infoWindowRef.current.open(mapRef.current, marker);
                google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                    const btn = document.getElementById(`applicant-map-view-${p.id}`);
                    if (btn) {
                        btn.addEventListener('click', () => {
                            if (typeof onApplicantClick === 'function') onApplicantClick(p.id);
                        });
                    }
                });
            });

            markersRef.current.push(marker);
            bounds.extend(position);
        });

        if (!bounds.isEmpty()) {
            mapRef.current.fitBounds(bounds, 60);
            // Avoid over-zooming when there's only one point.
            google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
                if (mapRef.current && mapRef.current.getZoom() > 14) mapRef.current.setZoom(14);
            });
        }
        };

        startMap();
        return () => { cancelled = true; };
    }, [open, mapsReady, points, onApplicantClick]);

    const clearMarkers = () => {
        markersRef.current.forEach((m) => {
            try { m.setMap(null); } catch (_) {}
        });
        markersRef.current = [];
    };

    const handleClose = () => {
        if (typeof onClose === 'function') onClose();
    };

    const showSpinner = (loading && !data) || (!mapsReady && !apiKeyMissing && !error);
    const geocodedNow = data?.geocodedNow || 0;
    const totalApplicants = data?.totalApplicants || 0;
    const geocodeApiUnavailable = !!data?.geocodeApiUnavailable;
    const serverGeocodingForbidden = !!data?.serverGeocodingForbidden;
    const pendingClientCount = Array.isArray(data?.pendingClientGeocode) ? data.pendingClientGeocode.length : 0;
    const isClientGeocoding = !!clientGeocodeProgress;
    const remainingClient = isClientGeocoding
        ? Math.max(0, clientGeocodeProgress.total - clientGeocodeProgress.done)
        : 0;
    // Entries we couldn't place anywhere (no precise geocode, no state for centroid fallback).
    const clientUnmappable = Math.max(
        0,
        pendingClientCount - clientPoints.length - centroidPoints.length - remainingClient,
    );
    const unmappableCount = (data?.unmappableCount || 0) + clientUnmappable;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile || fullScreen}
        >
            <DialogTitle sx={{ pr: 7 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Applicant Map</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {totalApplicants > 0
                                ? `Showing ${points.length} of ${totalApplicants} applicants${unmappableCount > 0 ? ` • ${unmappableCount} without a mappable address` : ''}`
                                : 'Loading applicants…'}
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {/* Status legend */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            {['new', 'in_review', 'approved', 'hired', 'rejected'].map((s) => (
                                <Stack key={s} direction="row" spacing={0.5} alignItems="center">
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS[s], border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)' }} />
                                    <Typography variant="caption" color="text.secondary">{STATUS_LABELS[s]}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                        <Tooltip title="Re-geocode all addresses">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => fetchPoints({ refresh: true })}
                                    disabled={loading || refreshing || apiKeyMissing}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {!isMobile && (
                            <Tooltip title={fullScreen ? 'Exit full screen' : 'Full screen'}>
                                <IconButton size="small" onClick={() => setFullScreen((v) => !v)}>
                                    {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, position: 'relative', height: { xs: 'calc(100vh - 120px)', md: fullScreen ? 'calc(100vh - 120px)' : '70vh' } }} data-usage-screen="recruitment.map">
                {apiKeyMissing && (
                    <Alert severity="warning" sx={{ m: 2 }}>
                        Map unavailable: <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> is not configured in the client environment.
                    </Alert>
                )}
                {!apiKeyMissing && error && (
                    <Alert severity="error" sx={{ m: 2 }} action={
                        <Button color="inherit" size="small" onClick={() => fetchPoints()}>Retry</Button>
                    }>
                        {error}
                    </Alert>
                )}
                {!apiKeyMissing && geocodeApiUnavailable && !error && (
                    <Alert severity="warning" sx={{ m: 2 }}>
                        Server cannot geocode addresses: <code>GOOGLE_MAPS_API_KEY</code> is missing on the backend.
                    </Alert>
                )}
                {!apiKeyMissing && geocodingApiUnavailable && !error && (
                    <Alert
                        severity="warning"
                        sx={{ m: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                component="a"
                                href="https://console.cloud.google.com/apis/library/geocoding-backend.googleapis.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Enable API
                            </Button>
                        }
                    >
                        <strong>Geocoding API is not enabled</strong> on your Google Cloud project. Showing approximate state-centroid dots for any applicant with a state. To get exact locations, enable the Geocoding API in Google Cloud Console and reopen this map.
                    </Alert>
                )}
                {!apiKeyMissing && serverGeocodingForbidden && !geocodingApiUnavailable && !error && (
                    <Alert severity="info" sx={{ m: 2 }}>
                        Server-side geocoding is blocked (HTTP 403). Falling back to in-browser geocoding{isClientGeocoding ? ` (${clientGeocodeProgress.done}/${clientGeocodeProgress.total})` : ''}.
                    </Alert>
                )}

                {showSpinner && (
                    <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 1,
                        bgcolor: 'rgba(255,255,255,0.85)', zIndex: 2,
                    }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary">
                            {geocodedNow > 0
                                ? `Geocoding ${geocodedNow} new address${geocodedNow === 1 ? '' : 'es'}…`
                                : 'Loading map…'}
                        </Typography>
                    </Box>
                )}

                {refreshing && !showSpinner && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.9)', px: 1.5, py: 0.5, borderRadius: 1, boxShadow: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">Re-geocoding…</Typography>
                    </Box>
                )}
                {isClientGeocoding && !showSpinner && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.95)', px: 1.5, py: 0.75, borderRadius: 1, boxShadow: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                            Geocoding in browser… {clientGeocodeProgress.done}/{clientGeocodeProgress.total}
                        </Typography>
                    </Box>
                )}

                {!apiKeyMissing && (
                    <Box ref={mapContainer} sx={{ width: '100%', height: '100%' }} />
                )}

                {!showSpinner && !error && !apiKeyMissing && points.length === 0 && data && (
                    <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 1,
                        bgcolor: 'rgba(255,255,255,0.85)', zIndex: 1, pointerEvents: 'none',
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>No mappable applicants</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {totalApplicants === 0
                                ? 'There are no applicants yet.'
                                : `${unmappableCount} applicant${unmappableCount === 1 ? '' : 's'} did not have an address that could be geocoded.`}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* Footer summary */}
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={`${points.length - centroidPoints.length} mapped`} color="primary" variant="outlined" />
                    {centroidPoints.length > 0 && (
                        <Chip size="small" label={`${centroidPoints.length} approximate (state)`} color="warning" variant="outlined" />
                    )}
                    {unmappableCount > 0 && (
                        <Chip size="small" label={`${unmappableCount} without address`} variant="outlined" />
                    )}
                    {geocodedNow > 0 && !loading && (
                        <Chip size="small" label={`Geocoded ${geocodedNow} new`} color="success" variant="outlined" />
                    )}
                </Stack>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchPoints({ refresh: true })}
                    disabled={loading || refreshing || apiKeyMissing}
                >
                    Refresh geocoding
                </Button>
            </Box>
        </Dialog>
    );
}

export default ApplicantMapDialog;
